import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Garden, Schedule, Zone, ZoneScheduleAssignment } from '@/types';
import { mockControllers, mockSchedules } from '@/data/mockData';
import { Calendar, Clock, Droplets, Timer, Plus, Layers, Check, CalendarDays, List } from 'lucide-react';
import { toast } from 'sonner';
import { GardenScheduleCalendar } from './GardenScheduleCalendar';

interface GardenSchedulesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  garden: Garden | null;
  onToggleSchedule?: (scheduleId: string, enabled: boolean) => void;
  onUpdateZoneSchedules?: (gardenId: string, zones: Zone[]) => void;
}

const DAYS_OF_WEEK = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

export function GardenSchedulesDialog({
  open,
  onOpenChange,
  garden,
  onToggleSchedule,
  onUpdateZoneSchedules,
}: GardenSchedulesDialogProps) {
  const [assignMode, setAssignMode] = useState(false);
  const [selectedZoneId, setSelectedZoneId] = useState<string | null>(null);
  const [selectedSchedules, setSelectedSchedules] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState('calendar');

  if (!garden) return null;

  // Get active schedules (global ones, not garden-specific for assignment)
  const activeSchedules = mockSchedules.filter((s) => s.enabled);

  const getControllerName = (controllerId?: string) => {
    if (!controllerId) return 'Sin asignar';
    const controller = mockControllers.find((c) => c.id === controllerId);
    return controller?.name || 'Desconocido';
  };

  const getValveName = (controllerId?: string, valveId?: string) => {
    if (!controllerId || !valveId) return null;
    const controller = mockControllers.find((c) => c.id === controllerId);
    const valve = controller?.valves.find((v) => v.id === valveId);
    return valve?.name;
  };

  const formatDays = (daysOfWeek: number[]) => {
    if (daysOfWeek.length === 7) return 'Todos los días';
    if (
      daysOfWeek.length === 5 &&
      !daysOfWeek.includes(0) &&
      !daysOfWeek.includes(6)
    )
      return 'Lunes a Viernes';
    if (
      daysOfWeek.length === 2 &&
      daysOfWeek.includes(0) &&
      daysOfWeek.includes(6)
    )
      return 'Fin de semana';
    return daysOfWeek.map((d) => DAYS_OF_WEEK[d]).join(', ');
  };

  const handleStartAssign = (zoneId: string) => {
    setSelectedZoneId(zoneId);
    const zone = garden.zones.find((z) => z.id === zoneId);
    const existingScheduleIds = zone?.scheduleAssignments?.map((sa) => sa.scheduleId) || [];
    setSelectedSchedules(new Set(existingScheduleIds));
    setAssignMode(true);
  };

  const handleCancelAssign = () => {
    setAssignMode(false);
    setSelectedZoneId(null);
    setSelectedSchedules(new Set());
  };

  const handleToggleScheduleSelection = (scheduleId: string) => {
    const newSelected = new Set(selectedSchedules);
    if (newSelected.has(scheduleId)) {
      newSelected.delete(scheduleId);
    } else {
      newSelected.add(scheduleId);
    }
    setSelectedSchedules(newSelected);
  };

  const handleConfirmAssign = () => {
    if (!selectedZoneId || !onUpdateZoneSchedules) return;

    const zone = garden.zones.find((z) => z.id === selectedZoneId);
    if (!zone) return;

    // Create schedule assignments for each selected schedule
    // Assign to all controller/valve combinations in the zone
    const newScheduleAssignments: ZoneScheduleAssignment[] = [];
    selectedSchedules.forEach((scheduleId) => {
      zone.controllerAssignments.forEach((ca) => {
        newScheduleAssignments.push({
          id: `sa-${Date.now()}-${scheduleId}-${ca.id}`,
          scheduleId,
          controllerId: ca.controllerId,
          valveChannel: ca.valveChannel,
        });
      });
    });

    const updatedZones = garden.zones.map((z) =>
      z.id === selectedZoneId
        ? { ...z, scheduleAssignments: newScheduleAssignments }
        : z
    );

    onUpdateZoneSchedules(garden.id, updatedZones);
    toast.success('Programaciones asignadas correctamente');
    handleCancelAssign();
  };

  const getScheduleById = (scheduleId: string) => {
    return mockSchedules.find((s) => s.id === scheduleId);
  };

  // View for assigning schedules to a zone
  if (assignMode && selectedZoneId) {
    const zone = garden.zones.find((z) => z.id === selectedZoneId);
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[550px] max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Calendar className="w-5 h-5 text-primary" />
              Asignar Programaciones
            </DialogTitle>
            <DialogDescription>
              Selecciona las programaciones activas para la zona "{zone?.name}"
            </DialogDescription>
          </DialogHeader>

          {activeSchedules.length === 0 ? (
            <div className="py-8 text-center">
              <Calendar className="w-10 h-10 text-muted-foreground/50 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">
                No hay programaciones activas disponibles
              </p>
            </div>
          ) : (
            <ScrollArea className="max-h-[350px] pr-4">
              <div className="space-y-2">
                {activeSchedules.map((schedule) => (
                  <div
                    key={schedule.id}
                    className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedSchedules.has(schedule.id)
                        ? 'border-primary bg-primary/5'
                        : 'hover:bg-muted/50'
                    }`}
                    onClick={() => handleToggleScheduleSelection(schedule.id)}
                  >
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={selectedSchedules.has(schedule.id)}
                        onCheckedChange={() => handleToggleScheduleSelection(schedule.id)}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm">{schedule.name}</div>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {schedule.startTime}
                          </span>
                          <span className="flex items-center gap-1">
                            <Timer className="w-3 h-3" />
                            {schedule.duration} min
                          </span>
                          <span>{formatDays(schedule.daysOfWeek)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={handleCancelAssign}>
              Cancelar
            </Button>
            <Button onClick={handleConfirmAssign} className="gap-2">
              <Check className="w-4 h-4" />
              Confirmar ({selectedSchedules.size})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // Main view with tabs
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Calendar className="w-5 h-5 text-primary" />
            Programaciones
          </DialogTitle>
          <DialogDescription>
            Gestiona las programaciones de riego para {garden.name}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 overflow-hidden flex flex-col">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="calendar" className="gap-2">
              <CalendarDays className="w-4 h-4" />
              Calendario
            </TabsTrigger>
            <TabsTrigger value="zones" className="gap-2">
              <List className="w-4 h-4" />
              Por Zonas
            </TabsTrigger>
          </TabsList>

          <TabsContent value="calendar" className="flex-1 overflow-y-auto mt-4">
            <GardenScheduleCalendar garden={garden} />
          </TabsContent>

          <TabsContent value="zones" className="flex-1 overflow-hidden mt-4">
            <ScrollArea className="h-[450px] pr-4">
              {/* Zones with their schedules */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Layers className="w-4 h-4 text-muted-foreground" />
                  <h4 className="font-medium text-sm">Programaciones por Zona</h4>
                </div>

                {garden.zones.length === 0 ? (
                  <div className="py-8 text-center border rounded-lg">
                    <Layers className="w-10 h-10 text-muted-foreground/50 mx-auto mb-3" />
                    <p className="text-sm text-muted-foreground">
                      Este jardín no tiene zonas configuradas
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {garden.zones.map((zone) => (
                      <div key={zone.id} className="p-4 rounded-lg border bg-card">
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <h5 className="font-medium">{zone.name}</h5>
                            <p className="text-xs text-muted-foreground">
                              {zone.controllerAssignments.length} controlador(es) asignado(s)
                            </p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleStartAssign(zone.id)}
                            className="gap-1"
                          >
                            <Plus className="w-3.5 h-3.5" />
                            Asignar
                          </Button>
                        </div>

                        {(!zone.scheduleAssignments || zone.scheduleAssignments.length === 0) ? (
                          <div className="p-3 rounded bg-muted/50 text-center">
                            <p className="text-xs text-muted-foreground">
                              Sin programaciones asignadas
                            </p>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {/* Group by unique scheduleId */}
                            {Array.from(new Set(zone.scheduleAssignments.map((sa) => sa.scheduleId))).map(
                              (scheduleId) => {
                                const schedule = getScheduleById(scheduleId);
                                if (!schedule) return null;
                                return (
                                  <div
                                    key={scheduleId}
                                    className="p-2 rounded bg-muted/30 flex items-center justify-between"
                                  >
                                    <div>
                                      <div className="text-sm font-medium">{schedule.name}</div>
                                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                        <span>{schedule.startTime}</span>
                                        <span>•</span>
                                        <span>{schedule.duration} min</span>
                                        <span>•</span>
                                        <span>{formatDays(schedule.daysOfWeek)}</span>
                                      </div>
                                    </div>
                                    <Badge
                                      variant={schedule.enabled ? 'default' : 'secondary'}
                                      className={
                                        schedule.enabled
                                          ? 'bg-success/10 text-success border-0 text-xs'
                                          : 'text-xs'
                                      }
                                    >
                                      {schedule.enabled ? 'Activa' : 'Inactiva'}
                                    </Badge>
                                  </div>
                                );
                              }
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                <Separator className="my-4" />

                {/* Garden-level schedules */}
                <div className="flex items-center gap-2">
                  <Droplets className="w-4 h-4 text-muted-foreground" />
                  <h4 className="font-medium text-sm">Programaciones del Jardín</h4>
                </div>

                {garden.schedules.length === 0 ? (
                  <div className="py-6 text-center border rounded-lg">
                    <Droplets className="w-8 h-8 text-muted-foreground/50 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">
                      No hay programaciones configuradas para este jardín
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {garden.schedules.map((schedule) => {
                      // Get first assignment for display, or show summary
                      const firstAssignment = schedule.assignments?.[0];
                      const controllerName = firstAssignment 
                        ? getControllerName(firstAssignment.controllerId) 
                        : 'Sin asignar';
                      const valveName = firstAssignment 
                        ? getValveName(firstAssignment.controllerId, firstAssignment.valveId) 
                        : null;
                      const assignmentCount = schedule.assignments?.length || 0;
                      
                      return (
                        <ScheduleCard
                          key={schedule.id}
                          schedule={schedule}
                          controllerName={controllerName}
                          valveName={valveName}
                          assignmentCount={assignmentCount}
                          formattedDays={formatDays(schedule.daysOfWeek)}
                          onToggle={onToggleSchedule}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            </ScrollArea>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

interface ScheduleCardProps {
  schedule: Schedule;
  controllerName: string;
  valveName: string | null;
  assignmentCount: number;
  formattedDays: string;
  onToggle?: (scheduleId: string, enabled: boolean) => void;
}

function ScheduleCard({
  schedule,
  controllerName,
  valveName,
  assignmentCount,
  formattedDays,
  onToggle,
}: ScheduleCardProps) {
  return (
    <div
      className={`p-3 rounded-lg border transition-colors ${
        schedule.enabled
          ? 'bg-card border-border'
          : 'bg-muted/30 border-muted opacity-60'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1.5 flex-1">
          <div className="flex items-center gap-2">
            <h4 className="font-medium text-sm">{schedule.name}</h4>
            <Badge
              variant={schedule.enabled ? 'default' : 'secondary'}
              className={
                schedule.enabled
                  ? 'bg-success/10 text-success border-0 text-xs'
                  : 'text-xs'
              }
            >
              {schedule.enabled ? 'Activa' : 'Inactiva'}
            </Badge>
          </div>

          <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
            <div className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              <span>{schedule.startTime}</span>
            </div>
            <div className="flex items-center gap-1">
              <Timer className="w-3 h-3" />
              <span>{schedule.duration} min</span>
            </div>
            <span>{formattedDays}</span>
          </div>

          <div className="flex flex-wrap gap-1.5">
            <Badge variant="outline" className="font-normal text-xs">
              {controllerName}
            </Badge>
            {valveName && (
              <Badge variant="outline" className="font-normal text-xs">
                {valveName}
              </Badge>
            )}
            {assignmentCount > 1 && (
              <Badge variant="secondary" className="font-normal text-xs">
                +{assignmentCount - 1} más
              </Badge>
            )}
          </div>
        </div>

        <Switch
          checked={schedule.enabled}
          onCheckedChange={(checked) => onToggle?.(schedule.id, checked)}
        />
      </div>
    </div>
  );
}
